import { ReactionType } from "@rosette/ts/src/business/game/ReactionType.js";
import type { StaticImageData } from "next/image";
import waveSvg from "@rosette/assets/src/reactions/wave.svg";
import hugSvg from "@rosette/assets/src/reactions/hug.svg";
import hugLottie from "@rosette/assets/src/reactions/hug.lottie.json";
import coolSvg from "@rosette/assets/src/reactions/cool.svg";
import coolLottie from "@rosette/assets/src/reactions/cool.lottie.json";
import meltingSvg from "@rosette/assets/src/reactions/melting.svg";
import meltingLottie from "@rosette/assets/src/reactions/melting.lottie.json";
import clapSvg from "@rosette/assets/src/reactions/clap.svg";
import clapLottie from "@rosette/assets/src/reactions/clap.lottie.json";
import shockedSvg from "@rosette/assets/src/reactions/shocked.svg";
import shockedLottie from "@rosette/assets/src/reactions/shocked.lottie.json";
import crySvg from "@rosette/assets/src/reactions/cry.svg";
import cryLottie from "@rosette/assets/src/reactions/cry.lottie.json";
import sparkleSvg from "@rosette/assets/src/reactions/sparkle.svg";
import sparkleLottie from "@rosette/assets/src/reactions/sparkle.lottie.json";
import crystalBallSvg from "@rosette/assets/src/reactions/crystal_ball.svg";
import crystalBallLottie from "@rosette/assets/src/reactions/crystal_ball.lottie.json";
import disorientedSvg from "@rosette/assets/src/reactions/disoriented.svg";
import disorientedLottie from "@rosette/assets/src/reactions/disoriented.lottie.json";
import turtleSvg from "@rosette/assets/src/reactions/turtle.svg";
import turtleLottie from "@rosette/assets/src/reactions/turtle.lottie.json";
import mind_blownSvg from "@rosette/assets/src/reactions/mind_blown.svg";
import mind_blownLottie from "@rosette/assets/src/reactions/mind_blown.lottie.json";
import resignSvg from "@rosette/assets/src/reactions/resign.svg";
import resignLottie from "@rosette/assets/src/reactions/resign.lottie.json";


interface ReactionOptions {
    loops?: number;
}


export class Reaction {
    public static WAVE = new Reaction(
        ReactionType.WAVE, waveSvg,
    );

    public static HUG = new Reaction(
        ReactionType.HUG, hugSvg, hugLottie,
    );

    public static readonly COOL = new Reaction(
        ReactionType.COOL, coolSvg, coolLottie,
    );

    public static readonly MELTING = new Reaction(
        ReactionType.MELTING, meltingSvg, meltingLottie,
    );

    public static readonly CLAP = new Reaction(
        ReactionType.CLAP, clapSvg, clapLottie,
        { loops: 4 },
    );

    public static readonly SHOCKED = new Reaction(
        ReactionType.SHOCKED, shockedSvg, shockedLottie,
    );

    public static readonly CRY = new Reaction(
        ReactionType.CRY, crySvg, cryLottie,
    );

    public static readonly SPARKLE = new Reaction(
        ReactionType.SPARKLE, sparkleSvg, sparkleLottie,
    );

    public static readonly CRYSTAL_BALL = new Reaction(
        ReactionType.CRYSTAL_BALL, crystalBallSvg, crystalBallLottie,
    );

    public static readonly DISORIENTED = new Reaction(
        ReactionType.DISORIENTED, disorientedSvg, disorientedLottie,
    );

    public static readonly TURTLE = new Reaction(
        ReactionType.TURTLE, turtleSvg, turtleLottie,
    );

    public static readonly MIND_BLOWN = new Reaction(
        ReactionType.MIND_BLOWN, mind_blownSvg, mind_blownLottie,
    );

    public static readonly RESIGN = new Reaction(
        ReactionType.RESIGN, resignSvg, resignLottie,
    );

    public static values(): Reaction[] {
        return [
            Reaction.WAVE,
            Reaction.HUG,
            Reaction.COOL,
            Reaction.MELTING,

            Reaction.CLAP,
            Reaction.SHOCKED,
            Reaction.CRY,
            Reaction.SPARKLE,

            Reaction.CRYSTAL_BALL,
            Reaction.DISORIENTED,
            Reaction.MIND_BLOWN,
            Reaction.TURTLE,

            Reaction.RESIGN,
        ];
    }

    private readonly type: ReactionType;
    private readonly image: StaticImageData;
    private readonly lottie?: object;
    private readonly options: ReactionOptions;

    constructor(
        type: ReactionType,
        image: StaticImageData,
        lottie?: object,
        options?: ReactionOptions,
    ) {
        this.type = type;
        this.image = image;
        this.lottie = lottie;
        this.options = options ?? {};
    }

    getType(): ReactionType {
        return this.type;
    }

    getImage(): StaticImageData {
        return this.image;
    }

    getLottie(): object | undefined {
        return this.lottie;
    }

    getOptions(): ReactionOptions {
        return this.options;
    }

    static get(type: ReactionType): Reaction {
        for (const reaction of Reaction.values()) {
            if (reaction.type === type)
                return reaction;
        }
        throw new Error(`Unknown reaction type, ${type.getID()}`);
    }
}
